/**
 * Offline draft queue — IndexedDB, hand-rolled rather than a dependency
 * (consistent with this codebase's preference elsewhere, e.g.
 * backend/src/utils/csv.js). Only ever holds *drafts*: invoice/gold-
 * purchase/repair forms fall back here when a save fails with no network,
 * never when issuing (allocating a real document number needs the server
 * in the loop at that moment — see the forms themselves).
 */
import { api } from './api.js';

const DB_NAME = 'vj-offline';
const DB_VERSION = 1;
const STORE = 'drafts';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * @param {object} args
 * @param {string} args.endpoint e.g. '/invoices'
 * @param {object} args.payload the exact body the form would have POSTed
 * @param {string} args.docType 'invoice' | 'gold_purchase' | 'repair' — for the sync-status label
 * @param {string} args.label a short human label, e.g. the customer's name
 */
export async function enqueueDraft({ endpoint, payload, docType, label }) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const req = tx.objectStore(STORE).add({ endpoint, payload, docType, label, queuedAt: new Date().toISOString() });
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

export async function listQueued() {
  const db = await openDb();
  try {
    const items = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).getAll();
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    return items.sort((a, b) => a.id - b.id);
  } finally {
    db.close();
  }
}

export async function removeQueued(id) {
  const db = await openDb();
  try {
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/**
 * Walks the queue in order, POSTing each to its original endpoint. Stops at
 * the first failure — preserves order (a later draft shouldn't land before
 * an earlier one) and avoids hammering a connection that's still bad.
 * @returns {Promise<number>} how many synced this pass
 */
export async function flushQueue() {
  const items = await listQueued();
  let synced = 0;
  for (const item of items) {
    try {
      await api.post(item.endpoint, item.payload);
      await removeQueued(item.id);
      synced += 1;
    } catch {
      break;
    }
  }
  return synced;
}

export default { enqueueDraft, listQueued, removeQueued, flushQueue };
